import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Calculator, TrendingUp, Hash, BarChart3 } from 'lucide-react';

/**
 * StatsSummary Component for numeric questions and general statistics
 * Handles number input, email, phone, and other data types
 */
const StatsSummary = ({ data, questionType }) => {

  if (!data || data.type === 'empty') {
    return (
      <div className="text-center py-4 text-muted-foreground">
        No data to analyze
      </div>
    );
  }

  // Handle numeric data (number inputs, calculated fields)
  const validCount = Array.isArray(data.validResponses) ? data.validResponses.length : Number(data.validResponses) || 0;
  
  if (data.type === 'numeric' && validCount > 0) {
    return (
      <div className="w-full">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-950 dark:to-blue-900 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Calculator className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              <p className="text-sm font-medium text-blue-800 dark:text-blue-200">Average</p>
            </div>
            <p className="text-2xl font-bold text-blue-900 dark:text-blue-100">
              {data.average || 0}
            </p>
          </div>

          <div className="p-4 bg-gradient-to-br from-green-50 to-green-100 dark:from-green-950 dark:to-green-900 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="h-5 w-5 text-green-600 dark:text-green-400" />
              <p className="text-sm font-medium text-green-800 dark:text-green-200">Median</p>
            </div>
            <p className="text-2xl font-bold text-green-900 dark:text-green-100">
              {data.median || 0}
            </p>
          </div>

          <div className="p-4 bg-gradient-to-br from-orange-50 to-orange-100 dark:from-orange-950 dark:to-orange-900 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Hash className="h-5 w-5 text-orange-600 dark:text-orange-400" />
              <p className="text-sm font-medium text-orange-800 dark:text-orange-200">Range</p>
            </div>
            <p className="text-xl font-bold text-orange-900 dark:text-orange-100">
              {data.min || 0} - {data.max || 0}
            </p>
          </div>

          <div className="p-4 bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-950 dark:to-purple-900 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <BarChart3 className="h-5 w-5 text-purple-600 dark:text-purple-400" />
              <p className="text-sm font-medium text-purple-800 dark:text-purple-200">Responses</p>
            </div>
            <p className="text-2xl font-bold text-purple-900 dark:text-purple-100">
              {validCount}
            </p>
          </div>
        </div>

        {/* Distribution insight */}
        <div className="mt-4 p-4 bg-muted/50 rounded-lg">
          <h4 className="font-medium mb-2">Distribution Insights</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-muted-foreground">Data Type:</p>
              <Badge variant="outline">{questionType || 'Numeric'}</Badge>
            </div>
            {validCount > 0 && (
              <div>
                <p className="text-muted-foreground">Valid Responses:</p>
                <p className="font-medium">{validCount} numeric values</p>
              </div>
            )}
          </div>
          
          {Number.isFinite(data.average) && Number.isFinite(data.median) && (
            <div className="mt-3 p-3 bg-background rounded border">
              <p className="text-xs text-muted-foreground mb-1">Distribution Skew:</p>
              {data.average === data.median ? (
                <p className="text-sm text-green-600 dark:text-green-400">
                  📊 Symmetric distribution (mean ≈ median)
                </p>
              ) : data.average > data.median ? (
                <p className="text-sm text-blue-600 dark:text-blue-400">
                  📈 Right-skewed (mean &gt; median)
                </p>
              ) : (
                <p className="text-sm text-orange-600 dark:text-orange-400">
                  📉 Left-skewed (mean &lt; median)
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Handle frequency data with summary stats
  if (data.type === 'frequency') {
    const entries = Object.entries(data.distribution || {});
    const total = entries.reduce((sum, [, count]) => sum + count, 0);
    const uniqueResponses = entries.length;

    return (
      <div className="w-full">
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="p-4 bg-gradient-to-br from-indigo-50 to-indigo-100 dark:from-indigo-950 dark:to-indigo-900 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Hash className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
              <p className="text-sm font-medium text-indigo-800 dark:text-indigo-200">Total</p>
            </div>
            <p className="text-2xl font-bold text-indigo-900 dark:text-indigo-100">
              {total}
            </p>
          </div>

          <div className="p-4 bg-gradient-to-br from-teal-50 to-teal-100 dark:from-teal-950 dark:to-teal-900 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <BarChart3 className="h-5 w-5 text-teal-600 dark:text-teal-400" />
              <p className="text-sm font-medium text-teal-800 dark:text-teal-200">Options</p>
            </div>
            <p className="text-2xl font-bold text-teal-900 dark:text-teal-100">
              {uniqueResponses}
            </p>
          </div>

          {data.mode && (
            <div className="p-4 bg-gradient-to-br from-amber-50 to-amber-100 dark:from-amber-950 dark:to-amber-900 rounded-lg lg:col-span-1 col-span-2">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                <p className="text-sm font-medium text-amber-800 dark:text-amber-200">Most Popular</p>
              </div>
              <p className="text-lg font-bold text-amber-900 dark:text-amber-100">
                {data.mode.length > 20 ? data.mode.substring(0, 20) + '...' : data.mode}
              </p>
              <p className="text-sm text-amber-700 dark:text-amber-300">
                {data.distribution[data.mode]} responses
              </p>
            </div>
          )}
        </div>

        {/* Top responses list */}
        <div className="mt-4 p-4 bg-muted/50 rounded-lg">
          <h4 className="font-medium mb-3">Response Distribution</h4>
          <div className="space-y-2">
            {entries
              .sort((a, b) => b[1] - a[1])
              .slice(0, 5)
              .map(([option, count]) => (
                <div key={option} className="flex justify-between items-center p-2 bg-background rounded border">
                  <span className="text-sm flex-1 min-w-0 mr-3">
                    {option.length > 40 ? option.substring(0, 40) + '...' : option}
                  </span>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Badge variant="secondary">{count}</Badge>
                    <span className="text-xs text-muted-foreground">
                      {Math.round((count / total) * 100)}%
                    </span>
                  </div>
                </div>
              ))}
            {entries.length > 5 && (
              <p className="text-xs text-muted-foreground text-center pt-2">
                ... and {entries.length - 5} more options
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Handle text data summary
  if (data.type === 'text') {
    return (
      <div className="w-full">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="p-4 bg-gradient-to-br from-violet-50 to-violet-100 dark:from-violet-950 dark:to-violet-900 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Hash className="h-5 w-5 text-violet-600 dark:text-violet-400" />
              <p className="text-sm font-medium text-violet-800 dark:text-violet-200">Responses</p>
            </div>
            <p className="text-2xl font-bold text-violet-900 dark:text-violet-100">
              {data.responseCount || 0}
            </p>
          </div>

          <div className="p-4 bg-gradient-to-br from-rose-50 to-rose-100 dark:from-rose-950 dark:to-rose-900 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <BarChart3 className="h-5 w-5 text-rose-600 dark:text-rose-400" />
              <p className="text-sm font-medium text-rose-800 dark:text-rose-200">Type</p>
            </div>
            <p className="text-lg font-bold text-rose-900 dark:text-rose-100">
              {questionType?.replace('_', ' ') || 'Text'}
            </p>
          </div>
        </div>

        {/* Sample responses */}
        {data.sampleResponses && data.sampleResponses.length > 0 && (
          <div className="mt-4 p-4 bg-muted/50 rounded-lg">
            <h4 className="font-medium mb-3">Sample Responses</h4>
            <div className="space-y-2">
              {data.sampleResponses.slice(0, 3).map((response, index) => (
                <div key={index} className="p-2 bg-background rounded border">
                  <p className="text-sm">
                    {typeof response === 'string' && response.length > 100 
                      ? response.substring(0, 100) + '...' 
                      : response
                    }
                  </p>
                </div>
              ))}
              {data.responseCount > 3 && (
                <p className="text-xs text-muted-foreground text-center pt-1">
                  ... and {data.responseCount - 3} more responses
                </p>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  // Generic fallback
  return (
    <div className="w-full">
      <div className="p-8 text-center bg-muted/50 rounded-lg">
        <BarChart3 className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
        <h3 className="font-medium mb-2">Data Summary</h3>
        <div className="text-sm text-muted-foreground mb-4">
          <span className="mr-2">Question type:</span>
          <Badge variant="outline">{questionType || data.type}</Badge>
        </div>
        {data.responseCount && (
          <p className="text-lg font-semibold">
            {data.responseCount} responses collected
          </p>
        )}
      </div>
    </div>
  );
};

export default StatsSummary;